import { useState } from "react";
import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AuthRepository } from "@/infrastructure/repositories/AuthRepository";
import { Password } from "@/domain/valueObjects/Password";
import { secureStorage } from "@/utils/secureStorage";

export function ChangePasswordContent() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
  showDialog("Missing Fields", "Please fill in all fields.", false);
  return;
}

if (!newPassword.trim() || !confirmPassword.trim()) {
  showDialog(
    "Invalid Password",
    "Password cannot contain only spaces.",
    false
  );
  return;
}

if (newPassword.length !== 6 || confirmPassword.length !== 6) {
  showDialog(
    "Invalid Password Length",
    "Password must be exactly 6 characters long.",
    false
  );
  return;
}

if (currentPassword === newPassword) {
  showDialog(
    "Invalid Password",
    "New password must be different from the current password.",
    false
  );
  return;
}

if (newPassword !== confirmPassword) {
  showDialog(
    "Password Mismatch",
    "New passwords do not match.",
    false
  );
  return;
}



    try {
      setIsLoading(true);
      const repo = new AuthRepository();
      const email = secureStorage.get<string>("current_user_email") || "";

      if (!email) {
        showDialog("Session Expired", "User email not found. Please log in again.", false);
        return;
      }

      const response = await repo.changePassword(
        email,
        new Password(currentPassword),
        new Password(newPassword)
      );

      if (response?.status === 1) {
        showDialog("Success", "Password updated successfully.", true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        showDialog("Error", "Old password is incorrect.", false);
      }
    } catch (error) {
      console.error("Change password failed:", error);
      showDialog("Error", "Failed to update password. Please try again.", false);
    } finally {
      setIsLoading(false);
    }
  };

  const showDialog = (title: string, message: string, success: boolean) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setIsSuccess(success);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Change Password</h1>
        <p className="text-muted-foreground">
          Update your password to keep your account secure
        </p>
      </div>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lock className="mr-2 h-5 w-5" />
            Security Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Account is Secure</h3>
              <p className="text-sm text-muted-foreground">
                Your password was last updated 30 days ago
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Form */}
      <Card>
        <CardHeader>
          <CardTitle>Update Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Current Password */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Current Password</label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Enter your current password"
                className="pr-10"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <label className="text-sm font-medium">New Password</label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter your new password"
                className="pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Confirm New Password</label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your new password"
                className="pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-4">
            <Button onClick={handleChangePassword} disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={isSuccess ? "text-green-600" : "text-red-600"}>
              {dialogTitle}
            </DialogTitle>
          </DialogHeader>

          <p className="text-muted-foreground">{dialogMessage}</p>

          <DialogFooter>
            <Button
              onClick={() => setDialogOpen(false)}
              className={isSuccess ? "bg-green-600 text-white" : "bg-red-600 text-white"}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
